import { FC, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";

import FooterText from "../../components/footerText";
import HubInputField from "../../components/inputType";

import { AuthContext } from "../../context/authContext";
import { ToastContext } from "../../context/toastContext";

import { LoginSchema } from "../../validation/login";
import useFetch from "../../hooks/useFetch";
import { APIEndpoints } from "../../types/apiTypes";
import { LoginFormType, LoginAPIResponseType } from "./types";
import { ToastTypes } from "../../types";
import { UserType } from "src/types/users";
import { USER_TYPES } from "src/constants";
import ContactAndSafetyInfo from "src/components/contactAndSafetyInfo";
import { TextAlignProps } from "src/components/contactAndSafetyInfo/types";
import { stringToBoolean } from "src/helpers";
import { parse } from "date-fns";
import { MannKindLogo } from "src/assets";



const HubLogin: FC = (): JSX.Element => {
  const [isPasswordVisible, setPasswordVisibility] = useState<boolean>(false);

  const { control, handleSubmit } = useForm({
    mode: 'all',
    resolver: yupResolver(LoginSchema),
  });
  const { userData: sessionSavedUser, createSession, restoreSession } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);
  const navigate = useNavigate();

  const reRouteRelevantUser = (userData: UserType) => {
     //console.log("UserData......", userData)
    if (!userData.is_password_changed) {
      navigate("/users/createuser");
    } else {
      if (userData.ref_type === 'Manufacturer') {
        navigate("/statistics");
      }
      else if (["patient_navigator", "kam","tbm", "rep"].includes(userData.ref_type.toLowerCase())) {
        navigate(`/statistics/${userData.id}`,{ replace: true });
      }
      // else if (userData.ref_type === 'tbm') {
      //   navigate(`/tbms/${userData.id}`);
      // }
       else if (userData.ref_type === 'dbm') {
        navigate(`/dbm/${userData.id}`);
      }

      else if (userData.ref_type === 'pn_lead') {
        navigate(`/patientNavigatorLeads/${userData.id}`);
      } else if (userData.ref_type === 'kam_lead') {
        navigate(`/kamleads/${userData.id}`);
      }
      else if (USER_TYPES.HCP.includes(userData.ref_type)) {
        if (!userData.baa_viewed || (userData.baa_viewed_date && userData.baa_viewed_date.length > 0 && parse(userData.baa_viewed_date, "MM/dd/yyyy", new Date()) > new Date())) {
          // window.alert("There has been a change to the privacy/terms and conditions. Please review and acknowledge the updated BAA before proceeding.")
          navigate("/pages/baa");
        } else {
          navigate("/cases");
        }
      } else {
        navigate("/cases");
      }
    }
  };

  const { data: userData, loading, mutate, error, removeErrorAndData } = useFetch<LoginAPIResponseType>(APIEndpoints.USER_SIGNIN);

  useEffect(() => {
    if (userData && userData.status.code === 200) {
      const { status, token } = userData;
      createSession(
        {
          ...status.data.user,
          practice_created_date: status.data.address ? status.data.address.date : "",
          practice_name: status.data.address ? status.data.address.name : "",
          address: status.data.address ? status.data.address.ref_address : "",
          baa_viewed: status.data.navbar ? stringToBoolean(status.data.navbar.baa_viewed) : false,
          baa_viewed_date: status.data.navbar ? status.data.navbar.baa_viewed_date : "",
          first_login_signature_check: status.data.navbar ? stringToBoolean(status.data.navbar.first_login_signature_check) : false,
          notifications_opt_out: status.data.navbar ? stringToBoolean(status.data.navbar.notifications_opt_out) : false,

          ...(
            status.data.navbar && status.data.navbar.case_draft_count ? { draft_count: status.data.navbar.case_draft_count, } : {}
          )
        },
        status.data.report_listing,
        status.data.sub_header,
        //status.data.navbar,
        token
      );
      showToast([status.message], ToastTypes.INFO);
      reRouteRelevantUser({
        ...status.data.user,
        baa_viewed: status.data.navbar ? stringToBoolean(status.data.navbar.baa_viewed) : false,
        baa_viewed_date: status.data.navbar ? status.data.navbar.baa_viewed_date : "",
        first_login_signature_check: status.data.navbar ? stringToBoolean(status.data.navbar.first_login_signature_check) : false,
        notifications_opt_out: status.data.navbar ? stringToBoolean(status.data.navbar.notifications_opt_out) : false,

      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const submitForm = async (values: LoginFormType) => {
    if ((error && error.length > 0) || userData) removeErrorAndData();
    const isSessionExists = restoreSession();

    if (!isSessionExists) {
      mutate({ user: values });
    } else {
      showToast([`There is already an active user session`], ToastTypes.INFO);
      if (sessionSavedUser) reRouteRelevantUser(sessionSavedUser);
    }
  };

  return (
    <div className="container-fluid login-sec pt-3 pt-lg-5 p-0 overflow-auto flex-fill d-flex flex-column">
      <div className="container flex-fill d-flex flex-column">
        <div className="row flex-fill">
          <div className="col-xl-8 col-lg-7 col-md-5 col-12 d-lg-block d-md-block d-none"></div>
          <div className="col-xl-4 col-lg-5 col-md-7 col-12">
            <form className="login-box bg-white rounded-3 p-4 shadow-lg" onSubmit={handleSubmit(submitForm)}>
              <h5 className="mb-0 fw-normal">Welcome to</h5>
              <img className="logo" src={MannKindLogo} alt="HeroImage" />
              <div className="mt-4">
                <Controller
                    name="email"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                        <HubInputField
                            inputId="email"
                            inputPlaceholder="Email"
                            isRowBased={false}
                            isRequired={true}
                            field={field}
                            error={error}
                            inputLabel="Email"
                            inputType="email"
                        />
                    )}
                />
                <Controller
                    name="password"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                        <>
                          <label htmlFor="password" className="form-label justify-content-start">
                            Password *
                          </label>
                          <div className="input-group mb-3">
                            <input
                                type={isPasswordVisible ? "text" : "password"} placeholder="Password"
                                className="form-control" {...field}
                            />
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => setPasswordVisibility((isPasswordVisible) => !isPasswordVisible)}
                            >
                              {isPasswordVisible ? (
                                  <i className="bi bi-eye-fill"></i>
                              ) : (
                                  <i className="bi bi-eye-slash"></i>
                              )}
                            </button>
                          </div>
                          {error && (<p className="mb-0 text-danger">{error.message}</p>)}
                        </>
                    )}
                />
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <button disabled={loading} className="btn btn-primary" type="submit">
                    {loading ? (<i className="spinner-border"></i>) : null} Log in
                  </button>
                  <p className="m-0 mt-2">
                    <Link to="/users/forgotpassword">Forgot your password?</Link>
                  </p>
                </div>
              </div>
            </form>
            <ContactAndSafetyInfo alignText={TextAlignProps.TEXT_CENTER} textColor="black" />
          </div>
        </div>
        <FooterText footerType="black" />
      </div>
    </div>
  );
};

export default HubLogin;
